// SportSwitcher — pill toggle that lets fans switch between sports on a
// tenant that runs more than one (e.g. MCL with basketball + soccer).
//
// Only renders when the tenant has 2+ active sport_ids. For single-sport
// tenants this is a no-op. A click persists the pick per host through
// SportProvider (`setActiveSport(id, { persist: true })`), so a return visit
// lands on the sport the fan was last reading. Restoring that pick is
// SportProvider's job too (site templates P0, §6.0); site chrome triggers it
// through useSiteNav (see `useStoredSportRestore`).

import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { useLeague } from '../contexts/LeagueContext'
import { useSportContext, sportDisplayName } from '../contexts/SportContext'
import { tenantSportIds } from '../contexts/storedSport'
import { useT } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

// Detail / slug-based routes where swapping the sport mid-page would 404
// (the entity belongs to one specific sport and has no equivalent in the
// other). On these the switcher is hidden — fans navigate up to a list
// page first. Deep-links to an off-sport entity auto-switch + toast (see
// the OffSportRedirect helper that detail pages mount).
//
// Every list / index route is intentionally absent so the toggle stays
// visible there and just swaps data in place (Home, /schedule, /players,
// /teams, /stats, /blog, /gallery, /live, /venues).
const HIDDEN_ON_PATTERNS = [
  '/players/:id',
  '/players/:id/*',
  '/teams/:id',
  '/teams/:id/*',
  '/game/:id',          // singular — matches App.tsx route
  '/game/:id/*',
  '/games/:id',         // defensive in case of /games refactor later
  '/games/:id/*',
  '/schedule/:id',      // drilled into one tournament
  '/pages/:slug',
  '/live/:slug',
  '/venues/:id',
  '/sponsor/:id',
  '/obs/*',             // OBS broadcast overlays — no fan navigation
  '/scorebug/*',
  '/share/*',           // share-card surfaces with sport baked into URL
]

// Per-sport emoji that goes inside the pill. Mirrors the score-bug
// highlightFormatter style — single character, neutral-friendly.
const SPORT_GLYPH: Record<string, string> = {
  basketball: '🏀',
  soccer: '⚽',
}

export default function SportSwitcher({ compact = false }: { compact?: boolean }) {
  const { league } = useLeague()
  const { activeSportId, setActiveSport } = useSportContext()
  const { pathname } = useLocation()
  const label = useT(siteCoreMessages).widgets.sportSwitcher.label

  // Distinct sport ids across the tenant's seasons. We surface a switcher
  // only when there's more than one to pick from.
  const sports = useMemo(() => tenantSportIds(league), [league])

  if (sports.length < 2) return null

  // Hide on entity / slug / embed routes where swapping the sport mid-page
  // would 404. Fans navigate up to a list page first.
  const isHidden = HIDDEN_ON_PATTERNS.some((pat) => matchPath({ path: pat, end: pat.endsWith('*') ? false : true }, pathname))
  if (isHidden) return null

  function select(id: string) {
    setActiveSport(id, { persist: true })
  }

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full border border-secondary/40 bg-bg-secondary/40 backdrop-blur-md p-1 ${
        compact ? 'text-[11px]' : 'text-xs'
      }`}
      role="tablist"
      aria-label={label}
    >
      {sports.map((id) => {
        const isActive = id === activeSportId
        return (
          <button
            key={id}
            role="tab"
            aria-selected={isActive}
            onClick={() => select(id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold uppercase tracking-wider transition-all ${
              isActive
                ? 'bg-primary text-bg-primary'
                : 'text-white/70 hover:text-white'
            }`}
          >
            <span className="text-base leading-none">{SPORT_GLYPH[id] ?? '🏆'}</span>
            <span>{sportDisplayName(id)}</span>
          </button>
        )
      })}
    </div>
  )
}
